import { useMemo, useCallback } from 'react';
import type { SavedConfig } from '../types';

export const useNonSpringRelatedAnimationConfig = (
  savedConfig: SavedConfig,
  updateSavedConfig: (newValue: SavedConfig) => void,
) => {
  const { defaultAnimationDelay, stringifiedLocalAnimationDelays, localIsAnimationDisabled } =
    savedConfig;

  const localAnimationDelays: number[] = useMemo(
    () => JSON.parse(stringifiedLocalAnimationDelays),
    [stringifiedLocalAnimationDelays],
  );

  const onChangeDefaultAnimationDelay = useCallback(
    (v: number) => updateSavedConfig({ ...savedConfig, defaultAnimationDelay: v }),
    [savedConfig, updateSavedConfig],
  );

  const onChangeAnimationDelay = useCallback(
    (index: number) => (v: number) =>
      updateSavedConfig({
        ...savedConfig,
        stringifiedLocalAnimationDelays: JSON.stringify(
          localAnimationDelays.map((x, i) => (i === index ? v : x)),
        ),
      }),
    [localAnimationDelays, savedConfig, updateSavedConfig],
  );

  const onClickResetAnimationDelay = useCallback(
    (index: number) => () =>
      updateSavedConfig({
        ...savedConfig,
        stringifiedLocalAnimationDelays: JSON.stringify(
          localAnimationDelays.map((x, i) => (i === index ? defaultAnimationDelay : x)),
        ),
      }),
    [defaultAnimationDelay, localAnimationDelays, savedConfig, updateSavedConfig],
  );

  const onClickResetAllAnimationDelays = useCallback(
    () =>
      updateSavedConfig({
        ...savedConfig,
        stringifiedLocalAnimationDelays: JSON.stringify(
          Array(localAnimationDelays.length).fill(defaultAnimationDelay),
        ),
      }),
    [defaultAnimationDelay, localAnimationDelays.length, savedConfig, updateSavedConfig],
  );

  const onClickIsAnimationDisabled = useCallback(
    (index: number) => () =>
      updateSavedConfig({
        ...savedConfig,
        localIsAnimationDisabled: [...localIsAnimationDisabled]
          .map((x, i) => (i === index ? String(1 - +x) : x))
          .join(''),
      }),
    [localIsAnimationDisabled, savedConfig, updateSavedConfig],
  );

  return {
    defaultAnimationDelay,
    stringifiedLocalAnimationDelays,
    localIsAnimationDisabled,
    onChangeDefaultAnimationDelay,
    onChangeAnimationDelay,
    onClickResetAnimationDelay,
    onClickResetAllAnimationDelays,
    onClickIsAnimationDisabled,
  };
};
